import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, AlertCircle, CheckCircle2, Loader2, Clock, FileText, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { capacityApi } from '../services';

/**
 * Team Capacity Dashboard - Resource planning overview
 * Shows workload, availability, and capacity for all team members
 */
const TeamCapacity = () => {
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);

  useEffect(() => {
    fetchTeamCapacity();
  }, []);

  const fetchTeamCapacity = async () => {
    setLoading(true);
    try {
      const data = await capacityApi.getTeamOverview();
      setTeamData(data);
    } catch (error) {
      console.error('Failed to fetch team capacity:', error);
      toast.error('Kunne ikke laste team-kapasitet');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'NORMAL':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'BUSY':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'OVERLOADED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'NORMAL':
        return <TrendingUp className="w-4 h-4" />;
      case 'BUSY':
      case 'OVERLOADED':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return 'Ledig';
      case 'NORMAL':
        return 'Normal';
      case 'BUSY':
        return 'Opptatt';
      case 'OVERLOADED':
        return 'Overbelastet';
      default:
        return status;
    }
  };

  const getSeniorityBadge = (seniority) => {
    const colors = {
      JUNIOR: 'bg-blue-100 text-blue-700',
      MEDIOR: 'bg-gray-100 text-gray-800',
      SENIOR: 'bg-purple-100 text-purple-700',
      PARTNER: 'bg-amber-100 text-amber-700'
    };
    
    return colors[seniority] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!teamData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">Kunne ikke laste team-kapasitet</p>
        </div>
      </div>
    );
  }

  const { lawyers, team_stats } = teamData;

  return (
    <div className="max-w-7xl mx-auto p-5 space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h1 className="text-lg font-semibold text-gray-900 mb-4">Team Kapasitet</h1>
        
        {/* Team Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{team_stats.total_lawyers}</div>
            <div className="text-xs text-gray-600">Totalt advokater</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{team_stats.available}</div>
            <div className="text-xs text-gray-600">Ledige</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{team_stats.overloaded}</div>
            <div className="text-xs text-gray-600">Overbelastet</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{team_stats.average_utilization}%</div>
            <div className="text-xs text-gray-600">Snitt utnyttelse</div>
          </div>
        </div>
      </div>

      {/* Lawyers List */}
      <div className="space-y-3">
        {lawyers.map((lawyer) => (
          <div
            key={lawyer.user_id}
            className="bg-white rounded-xl border border-gray-200 p-4 hover:border-gray-300 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              {/* Lawyer Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-sm font-semibold text-gray-800">
                    {lawyer.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{lawyer.name}</h3>
                    <p className="text-xs text-gray-600">{lawyer.email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getSeniorityBadge(lawyer.capacity_settings.seniority)}`}>
                    {lawyer.capacity_settings.seniority}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium border flex items-center gap-1 ${getStatusColor(lawyer.status)}`}>
                    {getStatusIcon(lawyer.status)}
                    {getStatusText(lawyer.status)}
                  </span>
                </div>

                {/* Practice Areas */}
                {lawyer.capacity_settings.practice_area_tags.length > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs text-gray-500">Fagområder:</span>
                    <div className="flex gap-1 flex-wrap">
                      {lawyer.capacity_settings.practice_area_tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Workload Stats */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">{lawyer.current_workload.active_cases} saker</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">{lawyer.current_workload.total_hours_this_week}t denne uken</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <ListChecks className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-600">{lawyer.current_workload.pending_tasks} oppgaver</span>
                  </div>
                </div>

                {/* Utilization Bar */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Utnyttelse</span>
                    <span className="text-xs font-semibold text-gray-900">
                      {lawyer.current_workload.utilization_percent}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        lawyer.current_workload.utilization_percent >= 100
                          ? 'bg-red-500'
                          : lawyer.current_workload.utilization_percent >= 80
                          ? 'bg-orange-500'
                          : lawyer.current_workload.utilization_percent >= 50
                          ? 'bg-blue-500'
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(lawyer.current_workload.utilization_percent, 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                    <span>{lawyer.current_workload.total_hours_this_week}t</span>
                    <span>{lawyer.capacity_settings.weekly_capacity_hours}t kapasitet</span>
                  </div>
                </div>
              </div>

              {/* Action Indicator */}
              <div className="flex-shrink-0">
                {lawyer.can_take_new_cases ? (
                  <div className="flex flex-col items-center justify-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mb-1" />
                    <span className="text-xs font-medium text-green-700">Kan ta saker</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-3 bg-red-50 rounded-lg border border-red-200">
                    <AlertCircle className="w-5 h-5 text-red-600 mb-1" />
                    <span className="text-xs font-medium text-red-700">Full kapasitet</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {lawyers.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">Ingen advokater i teamet</p>
        </div>
      )}
    </div>
  );
};

export default TeamCapacity;
